import { useState, useEffect } from "react";
import { Phone, FileText, Shield, CheckCircle2, Clock, Sparkles } from "lucide-react";
import { apiCall } from "../App";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { CouponInput } from "./CouponCurrency";

const DynamicCtaModal = ({ cta, open, onClose, token, user, activeModule }) => {
  const [loading, setLoading] = useState(false);
  const [selectedModule, setSelectedModule] = useState(activeModule || "relationship");
  const [couponCode, setCouponCode] = useState("");
  const [couponData, setCouponData] = useState(null);

  useEffect(() => {
    if (open) {
      setSelectedModule(activeModule || "relationship");
      setCouponCode("");
      setCouponData(null);
    }
  }, [open, activeModule]);

  if (!cta) return null;

  const sym = cta.currency === "USD" ? "$" : "\u20B9";
  const discount = couponData ? (couponData.discount_type === "percentage" ? Math.round(cta.price * couponData.discount_value / 100) : couponData.discount_value) : 0;
  const finalPrice = Math.max(cta.price - discount, 0);
  const gstAmount = Math.round(finalPrice * (cta.gst_percent / 100));
  const totalAmount = finalPrice + gstAmount;
  const moduleLabel = selectedModule === "life" ? "Life Wisdom Coaching" : "Relationship Wisdom Coaching";
  const orderType = cta.cta_type === "coaching_call" ? "coaching_call" : cta.cta_type === "report" ? "report" : "custom";

  const handleOrder = async () => {
    setLoading(true);
    const res = await apiCall("POST", "/cta-order/create", {
      cta_id: cta.cta_id,
      module: selectedModule,
      coupon_code: couponCode,
      currency: cta.currency,
    }, token);
    if (!res.success) {
      toast.error(res.error || "Failed to create order");
      setLoading(false);
      return;
    }

    const { razorpay_order_id, razorpay_key_id, order_id } = res.data;

    // Free order (100% coupon) — skip Razorpay, auto-verify
    if (res.data.total <= 0) {
      const verifyRes = await apiCall("POST", "/cta-order/verify", { order_id, razorpay_payment_id: "coupon_free" }, token);
      setLoading(false);
      if (verifyRes.success) {
        toast.success(verifyRes.data.message || "Order confirmed (free with coupon)!");
        onClose(true);
        if (verifyRes.data.booking_url) window.open(verifyRes.data.booking_url, "_blank");
      } else { toast.error(verifyRes.error || "Verification failed"); onClose(false); }
      return;
    }

    if (!razorpay_order_id || !razorpay_key_id) {
      toast.error("Payment gateway not configured");
      setLoading(false);
      return;
    }

    const options = {
      key: razorpay_key_id,
      amount: res.data.total * 100,
      currency: cta.currency,
      name: "SIWON HARMONY",
      description: `${cta.label} - ${moduleLabel}`,
      order_id: razorpay_order_id,
      prefill: { name: user?.name || "", email: user?.email || "" },
      theme: { color: "#B8860B" },
      handler: async (response) => {
        const verifyRes = await apiCall("POST", "/cta-order/verify", {
          order_id,
          razorpay_payment_id: response.razorpay_payment_id,
        }, token);
        setLoading(false);
        if (verifyRes.success) {
          toast.success(verifyRes.data.message || "Order confirmed!");
          onClose(true);
          if (verifyRes.data.booking_url) window.open(verifyRes.data.booking_url, "_blank");
        } else {
          toast.error(verifyRes.error || "Verification failed");
          onClose(false);
        }
      },
      modal: { ondismiss: () => { setLoading(false); } },
    };

    if (window.Razorpay) {
      const rzp = new window.Razorpay(options);
      rzp.open();
    } else {
      toast.error("Payment gateway loading...");
      setLoading(false);
    }
  };

  const Icon = cta.cta_type === "coaching_call" ? Phone : cta.cta_type === "report" ? FileText : Sparkles;

  return (
    <Dialog open={open} onOpenChange={() => onClose(false)}>
      <DialogContent className="bg-cosmic-deep border border-divine-gold/20 max-w-lg max-h-[90vh] overflow-y-auto mt-4" data-testid="dynamic-cta-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl text-divine-gold flex items-center gap-2">
            <Icon className="w-5 h-5" />
            {cta.label}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5 mt-2">
          {cta.description && (
            <p className="text-chakra-crown/70 text-sm leading-relaxed">{cta.description}</p>
          )}

          {cta.duration && cta.cta_type === "coaching_call" && (
            <div className="flex items-center gap-2 p-3 rounded-xl bg-divine-gold/5 border border-divine-gold/10">
              <Clock className="w-4 h-4 text-divine-gold" />
              <span className="text-chakra-crown text-sm">Duration: <strong className="text-divine-gold">{cta.duration}</strong></span>
            </div>
          )}

          {/* Module Selection */}
          <div>
            <p className="text-chakra-crown/50 text-xs font-heading tracking-wider mb-2">SELECT MODULE</p>
            <div className="grid grid-cols-2 gap-3">
              {[{ id: "relationship", label: "Relationship" }, { id: "life", label: "Life Wisdom Coaching" }].map(m => (
                <button key={m.id} onClick={() => setSelectedModule(m.id)}
                  className={`p-3 rounded-xl border text-sm font-medium transition-all ${selectedModule === m.id ? "border-divine-gold bg-divine-gold/10 text-divine-gold" : "border-divine-gold/10 text-chakra-crown/50 hover:border-divine-gold/30"}`}>
                  {m.label}
                </button>
              ))}
            </div>
          </div>

          {/* What You Get */}
          {cta.cta_type === "coaching_call" && (
            <div className="p-4 rounded-xl bg-cosmic-light/5 border border-divine-gold/10">
              <p className="text-divine-gold text-xs font-heading tracking-wider mb-3">WHAT YOU GET</p>
              {["1:1 session with your assigned SIWON coach", "Deep-dive into your assessment results", "Personalized action plan & guidance", "Follow-up notes from your coach"].map((item, i) => (
                <div key={i} className="flex items-start gap-2 mb-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 mt-0.5 shrink-0" /><span className="text-chakra-crown/70 text-xs">{item}</span></div>
              ))}
            </div>
          )}
          {cta.cta_type === "report" && (
            <div className="p-4 rounded-xl bg-cosmic-light/5 border border-divine-gold/10">
              <p className="text-divine-gold text-xs font-heading tracking-wider mb-3">WHAT YOU GET</p>
              {["Auto-generated report from your assessment data", "Scriptural references & 3A framework insights", "Coach review, refinement & personalization", "Downloadable PDF report"].map((item, i) => (
                <div key={i} className="flex items-start gap-2 mb-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 mt-0.5 shrink-0" /><span className="text-chakra-crown/70 text-xs">{item}</span></div>
              ))}
            </div>
          )}

          {/* Coupon */}
          <CouponInput token={token} module={selectedModule} currency={cta.currency} orderType={orderType}
            onApply={(code, data) => { setCouponCode(code); setCouponData(data); }}
            onClear={() => { setCouponCode(""); setCouponData(null); }} />

          {/* Pricing */}
          <div className="p-4 rounded-xl bg-divine-gold/5 border border-divine-gold/20">
            <div className="flex justify-between text-sm text-chakra-crown/70 mb-1">
              <span>{cta.label}</span>
              <span>{sym}{cta.price}</span>
            </div>
            {discount > 0 && (
              <div className="flex justify-between text-sm text-emerald-400 mb-1">
                <span>Discount ({couponCode})</span>
                <span>-{sym}{discount}</span>
              </div>
            )}
            <div className="flex justify-between text-sm text-chakra-crown/50 mb-2">
              <span>GST ({cta.gst_percent}%)</span>
              <span>{sym}{gstAmount}</span>
            </div>
            <div className="border-t border-divine-gold/20 pt-2 flex justify-between text-base font-bold text-divine-gold">
              <span>Total</span>
              <span>{sym}{totalAmount}</span>
            </div>
          </div>

          <Button onClick={handleOrder} disabled={loading} data-testid="dynamic-cta-pay-btn"
            className="w-full bg-divine-gold hover:bg-divine-gold/90 text-cosmic-void font-heading text-base py-6">
            {loading ? "Processing..." : `Pay ${cta.currency} ${totalAmount}`}
          </Button>

          <div className="flex items-center justify-center gap-1.5 text-chakra-crown/40 text-[10px]">
            <Shield className="w-3 h-3" />Secured by Razorpay
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DynamicCtaModal;
